import React, { useMemo, useState } from "react";

const createOptionId = () => `opt-${Math.random().toString(36).slice(2, 10)}`;

const normalizeValue = (value, allowMultipleDefault = false) => {
  if (!value || typeof value !== "object") {
    return {
      allowMultiple: allowMultipleDefault,
      options: [],
      selected: [],
    };
  }
  const allowMultiple = Boolean(value.allowMultiple ?? allowMultipleDefault);
  const rawOptions = Array.isArray(value.options) ? value.options : [];
  const options = rawOptions.map((option) => {
    if (!option) {
      return null;
    }
    if (typeof option === "string") {
      return { id: createOptionId(), label: option };
    }
    const id = option.id ? String(option.id) : createOptionId();
    const label = option.label ?? option.name ?? option.value;
    if (typeof label !== "string") {
      return null;
    }
    return { id, label };
  });
  const filteredOptions = options.filter(Boolean);
  const optionIds = new Set(filteredOptions.map((option) => option.id));
  const selected = Array.isArray(value.selected)
    ? value.selected.map((id) => String(id)).filter((id) => optionIds.has(id))
    : [];
  return {
    allowMultiple,
    options: filteredOptions,
    selected,
  };
};

const DynamicSelectionField = ({
  value,
  onChange,
  readOnly,
  allowMultipleDefault = false,
  required,
  allowSelectionWhenReadOnly = false,
}) => {
  const [optionInput, setOptionInput] = useState("");

  const normalized = useMemo(
    () => normalizeValue(value, allowMultipleDefault),
    [value, allowMultipleDefault],
  );

  const emitChange = (nextValue) => {
    if (onChange) {
      onChange(nextValue);
    }
  };

  const handleAddOption = () => {
    const trimmed = optionInput.trim();
    if (!trimmed) return;
    const next = {
      ...normalized,
      options: [...normalized.options, { id: createOptionId(), label: trimmed }],
    };
    emitChange(next);
    setOptionInput("");
  };

  const handleRemoveOption = (id) => {
    const remaining = normalized.options.filter((option) => option.id !== id);
    const remainingIds = new Set(remaining.map((option) => option.id));
    const selected = normalized.selected.filter((selectedId) => remainingIds.has(selectedId));
    emitChange({
      ...normalized,
      options: remaining,
      selected,
    });
  };

  const handleToggleAllowMultiple = (event) => {
    emitChange({
      ...normalized,
      allowMultiple: event.target.checked,
      selected: event.target.checked ? normalized.selected : normalized.selected.slice(0, 1),
    });
  };

  const handleSelectChange = (id, checked) => {
    if (normalized.allowMultiple) {
      const set = new Set(normalized.selected);
      if (checked) set.add(id);
      else set.delete(id);
      emitChange({ ...normalized, selected: Array.from(set) });
    } else {
      emitChange({ ...normalized, selected: checked ? [id] : [] });
    }
  };

  const hasOptions = normalized.options.length > 0;
  const selectedSet = new Set(normalized.selected);

  const selectionDisabled = readOnly && !allowSelectionWhenReadOnly;

  return (
    <div className="rounded border border-slate-200 bg-white/60 p-3 shadow-sm">
      {!readOnly && (
        <div className="mb-3 space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={optionInput}
              onChange={(event) => setOptionInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddOption();
                }
              }}
              placeholder="Adicionar nova opção"
              className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={handleAddOption}
              className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Adicionar opção
            </button>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={normalized.allowMultiple}
              onChange={handleToggleAllowMultiple}
            />
            Permitir múltiplas seleções na próxima etapa
          </label>
        </div>
      )}

      {hasOptions ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700">
            {normalized.allowMultiple
              ? "Selecione uma ou mais opções disponíveis"
              : "Selecione uma das opções disponíveis"}
            {required && <span className="ml-1 text-red-600">*</span>}
          </p>
          <ul className="space-y-1">
            {normalized.options.map((option) => (
              <li key={option.id} className="flex items-center justify-between rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                <label className="flex flex-1 items-center gap-2">
                  <input
                    type={normalized.allowMultiple ? "checkbox" : "radio"}
                    name="dynamic-selection"
                    value={option.id}
                    checked={selectedSet.has(option.id)}
                    onChange={(event) => handleSelectChange(option.id, event.target.checked)}
                    disabled={selectionDisabled}
                  />
                  <span>{option.label}</span>
                </label>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(option.id)}
                    className="text-xs font-medium text-rose-600 transition hover:text-rose-700"
                  >
                    Remover
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          {!readOnly
            ? "Adicione opções acima para que o próximo responsável possa selecioná-las."
            : "Nenhuma opção foi configurada até o momento."}
        </p>
      )}
    </div>
  );
};

export default DynamicSelectionField;
